import { Skeleton, Tooltip } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import moment from "moment-jalaali";
import { _getHeaderColor } from "../../utils/helpers";
import EmptyState from "../EmptyState";
import styles from "./styles.module.css";

const FALLBACK_COLOR = "#94a3b8";

const formatDuration = (totalSeconds) => {
  const s = Number(totalSeconds) || 0;
  if (s < 60) return `${s} ثانیه`;

  const hours = Math.floor(s / 3600);
  const minutes = Math.round((s % 3600) / 60);

  if (!hours) return `${minutes} دقیقه`;
  return minutes ? `${hours} ساعت و ${minutes} دقیقه` : `${hours} ساعت`;
};

const Timesheet = ({ days = [], loading = false }) => {
  if (loading) {
    return (
      <div className={styles.wrap}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} active paragraph={{ rows: 1 }} />
        ))}
      </div>
    );
  }

  if (!days.length)
    return (
      <EmptyState
        compact
        icon={<ClockCircleOutlined />}
        title="زمانی ثبت نشده"
        description="وقتی روی تسک‌ها تایمر بزنی یا زمان ثبت کنی، اینجا نمایش داده می‌شه."
      />
    );

  const busiest = Math.max(...days.map((d) => d.seconds || 0), 1);
  const today = moment().format("YYYY-MM-DD");

  return (
    <div className={styles.wrap}>
      {days.map((day) => {
        const seconds = day.seconds || 0;
        const isEmpty = !day.tasks?.length;
        const isToday = day.date === today;
        const m = moment(day.date, "YYYY-MM-DD");

        return (
          <div
            key={day.date}
            className={`${styles.day} ${isEmpty ? styles.dayEmpty : ""} ${
              isToday ? styles.dayToday : ""
            }`}
          >
            <div className={styles.head}>
              <div className={styles.date}>
                <span className={styles.weekday}>{m.format("dddd")}</span>
                <span className={styles.fullDate}>
                  {m.format("jD jMMMM jYYYY")}
                </span>
                {isToday && <span className={styles.todayChip}>امروز</span>}
              </div>

              <div className={styles.meta}>
                {isEmpty ? (
                  <span className={styles.emptyText}>بدون فعالیت</span>
                ) : (
                  <>
                    <span className={styles.count}>
                      {day.tasks.length} تسک
                    </span>
                    <span className={styles.total}>
                      {formatDuration(seconds)}
                    </span>
                  </>
                )}
              </div>
            </div>

            {!isEmpty && (
              <>
                {}
                <div
                  className={styles.track}
                  style={{ width: `${Math.max((seconds / busiest) * 100, 6)}%` }}
                >
                  {day.tasks.map((task, i) => (
                    <Tooltip
                      key={`${task.title}-${i}`}
                      title={`${task.title} — ${formatDuration(task.seconds)}`}
                    >
                      <span
                        className={styles.segment}
                        style={{
                          width: `${
                            seconds ? ((task.seconds || 0) / seconds) * 100 : 0
                          }%`,
                          background: task.step?.color_code || FALLBACK_COLOR,
                        }}
                      />
                    </Tooltip>
                  ))}
                </div>

                <div className={styles.tasks}>
                  {day.tasks.map((task, i) => {
                    const color = task.step?.color_code || FALLBACK_COLOR;
                    const share = seconds
                      ? Math.round(((task.seconds || 0) / seconds) * 100)
                      : 0;

                    return (
                      <div className={styles.task} key={`${task.title}-${i}`}>
                        <span
                          className={styles.dot}
                          style={{ background: color }}
                        />
                        <span className={styles.taskTitle} title={task.title}>
                          {task.title}
                        </span>
                        {task.step?.title && (
                          <span
                            className={styles.step}
                            style={{
                              background: color,
                              color: _getHeaderColor(color),
                            }}
                          >
                            {task.step.title}
                          </span>
                        )}
                        {day.tasks.length > 1 && (
                          <span className={styles.taskShare}>٪{share}</span>
                        )}
                        <span className={styles.taskTime}>
                          {formatDuration(task.seconds)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Timesheet;
